import { twJoin } from '@troon/tailwind-preset/merge';
import { Match, splitProps, Switch } from 'solid-js';
import { Picture } from '../components';
import { VimeoVideo } from '../video/vimeo';
import { YoutubeVideo } from '../video/youtube';
import { Video } from '../video/video';
import type { ComponentProps, ParentProps } from 'solid-js';

type Props = ParentProps<{
	appearance?: 'primary' | 'secondary' | 'default' | 'contained';
	aspectRatio?: 'square' | '4/3' | 'video';
	reverse?: boolean;
}>;

type Photo = Props &
	Partial<ComponentProps<typeof Picture>> & {
		type?: 'photo';
		src: ComponentProps<typeof Picture>['src'];
	};

type YoutubeVideoProps = Props &
	Partial<ComponentProps<typeof YoutubeVideo>> & {
		type: 'video';
		videoProvider: 'youtube';
	};

type VimeoVideoProps = Props &
	Partial<ComponentProps<typeof YoutubeVideo>> & {
		type: 'video';
		videoProvider: 'vimeo';
	};

type CdnVideoProps = Props &
	Partial<ComponentProps<typeof Video>> & {
		type: 'video';
		videoProvider: 'cdn';
	};

export function MediaSection(props: Photo | YoutubeVideoProps | VimeoVideoProps | CdnVideoProps) {
	const [, pictureProps] = splitProps(props, ['children', 'reverse', 'type']);
	return (
		<div
			class={twJoin(
				'@container',
				props.appearance && props.appearance !== 'default' && 'rounded-lg p-8',
				props.appearance === 'primary' && 'bg-brand-700 text-white',
				props.appearance === 'secondary' && 'bg-neutral-100',
				props.appearance === 'contained' && 'border border-neutral bg-white p-4 md:p-8',
			)}
		>
			<div class="grid grid-cols-1 gap-x-16 gap-y-8 @3xl:grid-cols-2 @4xl:gap-x-36">
				<div class={twJoin('flex flex-col justify-center gap-8', props.reverse && 'order-2')}>{props.children}</div>
				<div class={twJoin('flex flex-col justify-center gap-4 @xl:gap-8', props.reverse && 'order-1')}>
					<Switch>
						<Match when={(props.type === 'photo' || !props.type) && (pictureProps as ComponentProps<typeof Picture>)}>
							{(pictureProps) => (
								<Picture
									width={480}
									height={360}
									{...pictureProps}
									sizes="(min-width: 768px) 45vw, 95vw"
									class={twJoin(
										'w-full overflow-hidden rounded-lg object-contain',
										(!props.aspectRatio || props.aspectRatio === '4/3') && 'aspect-[4/3]',
										props.aspectRatio === 'video' && 'aspect-video',
										props.aspectRatio === 'square' && 'aspect-square',
									)}
								/>
							)}
						</Match>
						<Match
							when={
								props.type === 'video' &&
								props.videoProvider === 'vimeo' &&
								(props as ComponentProps<typeof VimeoVideo>)
							}
						>
							{(videoProps) => <VimeoVideo {...videoProps()} />}
						</Match>
						<Match
							when={
								props.type === 'video' &&
								props.videoProvider === 'youtube' &&
								(props as ComponentProps<typeof YoutubeVideo>)
							}
						>
							{(videoProps) => <YoutubeVideo {...videoProps()} />}
						</Match>
						<Match
							when={props.type === 'video' && props.videoProvider === 'cdn' && (props as ComponentProps<typeof Video>)}
						>
							{(videoProps) => <Video {...videoProps()} />}
						</Match>
					</Switch>
				</div>
			</div>
		</div>
	);
}
